import * as React from 'react';

import { Tooltip, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import { visuallyHidden } from '@mui/utils';
import dayjs from 'dayjs';

interface HeadCellsProps {
  id: string;
  label: string;
  tooltip?: string;
}

type Order = 'asc' | 'desc';

interface SortingTableProps {
  dados: any;
  headCells: HeadCellsProps[];
  defaultOrderBy?: string;
  defaultOrderDir?: Order;
}

interface EnhancedTableProps {
  headCells: HeadCellsProps[];
  order: Order;
  orderBy: string;
  onRequestSort: (event: React.MouseEvent<unknown>, property: string) => void;
}

const isStringDate = (dateString: string) => {
  const dateRegex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/;
  return dateRegex.test(dateString);
};

const formatDate = (date: string) => {
  const dateArr = date.split('/');
  return `${dateArr[2]}-${dateArr[1]}-${dateArr[0]}`;
};

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  const aValue = a[orderBy];
  const bValue = b[orderBy];

  if (
    typeof aValue === 'string' &&
    typeof bValue === 'string' &&
    isStringDate(aValue) &&
    isStringDate(bValue)
  ) {
    const formattedA = formatDate(aValue);
    const formattedB = formatDate(bValue);

    const dayjsA = dayjs(formattedA);
    const dayjsB = dayjs(formattedB);

    if (dayjsB.isBefore(dayjsA)) {
      return -1;
    } else if (dayjsB.isAfter(dayjsA)) {
      return 1;
    }
    return 0;
  } else {
    if (bValue < aValue) {
      return -1;
    }
    if (bValue > aValue) {
      return 1;
    }
    return 0;
  }
}

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (a: { [key in Key]: number | string }, b: { [key in Key]: number | string }) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const orderResult = comparator(a[0], b[0]);
    if (orderResult !== 0) return orderResult;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const { headCells, order, orderBy, onRequestSort } = props;
  const createSortHandler = (property: string) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead sx={{ bgcolor: '#fafafa' }}>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align="left"
            padding="normal"
            sortDirection={orderBy === headCell.id ? order : false}
            sx={{ color: '#000', fontWeight: 'bold' }}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              <Tooltip title={headCell.tooltip ?? ''} placement="top">
                <span>{headCell.label}</span>
              </Tooltip>
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'ordenado decrescente' : 'ordenado crescente'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

// Function to render table cells with null/empty value handling
const renderTableCells = (row: any, headCells: HeadCellsProps[]) => {
  return headCells.map((headCell) => {
    const cellValue = row[headCell.id];
    const displayValue =
      cellValue !== null && cellValue !== undefined && cellValue !== '' ? cellValue : '-';

    return (
      <TableCell key={headCell.id} align="left">
        {displayValue}
      </TableCell>
    );
  });
};

export default function SortingTable({
  dados,
  headCells,
  defaultOrderBy = '',
  defaultOrderDir = 'asc',
}: SortingTableProps) {
  const [order, setOrder] = React.useState<Order>(defaultOrderDir);
  const initialOrderBy = headCells.some((headCell) => headCell.id === defaultOrderBy)
    ? defaultOrderBy
    : headCells[0].id;
  const [orderBy, setOrderBy] = React.useState<string>(initialOrderBy);

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const rows = dados.map((dado: any, i: number) => ({ id: i + 1, ...dado }));

  return (
    <Box sx={{ width: '100%' }}>
      <Paper elevation={0} sx={{ width: '100%' }}>
        <TableContainer>
          <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size="medium">
            <EnhancedTableHead
              headCells={headCells}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {rows.length !== 0 ? (
                stableSort(rows, getComparator(order, orderBy)).map((row: any) => (
                  <TableRow key={row.id}>{renderTableCells(row, headCells)}</TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={headCells.length}>
                    <Typography fontSize={14} textAlign="center" color="#616161">
                      Nenhum registro encontrado
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
